import { useEffect, useMemo, useRef, useCallback, useState } from 'react';
import { Outlet } from 'react-router-dom';
import isEqual from 'lodash.isequal';
import {
  IconLoader,
  Table,
  useFilters,
  useQueryState,
  useScroll,
  usePrevious,
  ButtonSecondary,
  ButtonSize,
  useGetList,
  Popup,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import {
  currenciesListColumns,
  currenciesListColumnsLabels,
  currenciesListFiltersSchema,
  currenciesListSchema,
} from '../variables/currencies';
import { onSelectedRows } from '../store/slices/currencies';
import {
  useLazyGetEmployeesBaseCurrencyQuery,
  useLazyGetCountActiveCurrenciesQuery,
  useLazyGetEmployeesCurrenciesQuery,
  useToggleCurrenciesStatusMutation,
} from '../store/services/currencies';
import { CurrencyItem } from '../types';
import formatCurrenciesList from '../helpers/format-currencies-list';

import cn from '../styles.module.css';

const CurrenciesList = () => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [activatePopupVisible, setActivatePopupVisible] = useState(false);
  const [disablePopupVisible, setDisablePopupVisible] = useState(false);

  const {
    requestInProgress,
    pagination,
    values: currencies,
    selectedCurrencyIds,
    baseCurrency,
    activeCount,
    disableActivateBtn,
    disableInactivateBtn,
  } = useAppSelector((state) => state.settings.currencies);

  const [getList, { isFetching, isLoading }] =
    useLazyGetEmployeesCurrenciesQuery();
  const [getEmployeesBaseCurrency] = useLazyGetEmployeesBaseCurrencyQuery();
  const [getCountActiveCurrencies] = useLazyGetCountActiveCurrenciesQuery();
  const [toggledCurrenciesActive] = useToggleCurrenciesStatusMutation();

  const dispatch = useAppDispatch();

  const { filters, sort, onSortChange, onFilterChange } =
    useQueryState(currenciesListSchema);

  const prevFilters = usePrevious(filters);

  const formattedData = useMemo(() => {
    const data = formatCurrenciesList(currencies as CurrencyItem[]);

    return data;
  }, [currencies, sort, filters, activeCount]);

  const onRowsSelect = (rows: string[]) => {
    if (!isEqual(rows, selectedCurrencyIds)) {
      dispatch(onSelectedRows(rows));
    }
  };

  const { getListAction, setPage, onFilter, onSort, errorList } =
    useGetList<CurrencyItem>({
      getList,
      onFilterChange,
      onSortChange,
      schema: currenciesListSchema,
    });

  const getEmployeesBaseCurrencyAction = useCallback(() => {
    getEmployeesBaseCurrency();
  }, []);

  const getCountActiveCurrenciesAction = useCallback(() => {
    getCountActiveCurrencies();
  }, []);

  const { filtersComponent, handleSubmit } = useFilters({
    filtersSchema: currenciesListFiltersSchema,
    filters,
    onFilterChange: onFilter,
  });

  useScroll({
    element: scrollRef,
    requestInProgress,
  });

  useEffect(() => {
    if (
      prevFilters &&
      !Object.keys(prevFilters).length &&
      filters &&
      !currencies
    ) {
      getListAction({
        filters,
        sort,
      });
    }
  }, [filters, prevFilters, currencies]);

  useEffect(() => {
    getEmployeesBaseCurrencyAction();
    getCountActiveCurrenciesAction();
  }, [getEmployeesBaseCurrencyAction, getCountActiveCurrenciesAction]);

  const onActionCurrencies = (value: boolean) => {
    if (selectedCurrencyIds) {
      toggledCurrenciesActive({ active: value, ids: selectedCurrencyIds });
      setActivatePopupVisible(false);
      setDisablePopupVisible(false);

      setTimeout(() => {
        getListAction({
          filters,
          sort,
        });
        getCountActiveCurrenciesAction();
      });
    }
  };

  useEffect(() => {
    let title = 'Currencies';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let content: React.JSX.Element;

  if (isLoading) {
    content = (
      <div className={cn.tabLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <Table
        columns={currenciesListColumns}
        data={formattedData || []}
        onSort={(name) => onSort(name)}
        onRowsSelect={onRowsSelect}
        filters={filters}
        sort={sort}
        columnsLabels={currenciesListColumnsLabels}
        onFilterChange={(filterValues) => onFilter(filterValues)}
        filtersComponent={filtersComponent}
        handleSubmit={handleSubmit}
        isLoading={isFetching}
        take="30"
        setPage={(data) => setPage({ ...data, filters })}
        pagination={pagination}
        error={errorList}
        clickable
      />
    );
  }

  return (
    <div className={cn.currenciesWrapper}>
      <div className={cn.settingsTitle}>Currencies</div>
      <div className={cn.currenciesInfo}>
        <div className={cn.infoWrapper}>
          <div className={cn.infoTitle}>Base currency:</div>
          {baseCurrency && (
            <div className={cn.infoValue}>
              {`${baseCurrency?.description} (${baseCurrency?.name}), ${baseCurrency?.code}`}
            </div>
          )}
        </div>
        <div className={cn.infoWrapper}>
          <div className={cn.infoTitle}>Number of available currencies:</div>
          {activeCount && (
            <div
              className={cn.infoValue}
            >{`${activeCount} active currencies`}</div>
          )}
        </div>
      </div>

      <div className={cn.buttons}>
        <ButtonSecondary
          text="Activate"
          size={ButtonSize.SMALL}
          disabled={disableActivateBtn}
          onClick={() => setActivatePopupVisible(true)}
        />
        <ButtonSecondary
          text="Disable"
          size={ButtonSize.SMALL}
          disabled={disableInactivateBtn}
          onClick={() => setDisablePopupVisible(true)}
        />
      </div>

      <div className={cn.contentWrapper} ref={scrollRef}>
        {content}
      </div>

      <Popup
        title="You activate currencies"
        okButtonText="Yes, activate"
        description="Currencies will become available for transactions. Activate?"
        cancelButtonText="Cancel"
        onCancelClick={() => setActivatePopupVisible(false)}
        onOkClick={() => onActionCurrencies(true)}
        isVisible={activatePopupVisible}
      />
      <Popup
        title="You disable off the currency"
        description="The currency will not be available for exchange and for transactions. Disable?"
        okButtonText="Yes, disable"
        cancelButtonText="Cancel"
        onCancelClick={() => setDisablePopupVisible(false)}
        onOkClick={() => onActionCurrencies(false)}
        isVisible={disablePopupVisible}
      />
      <Outlet />
      <div id="currency-popup" />
    </div>
  );
};

export default CurrenciesList;
